import axios from 'axios';
import { activeRole, readSession, clearSession, loginPathFor } from './session';
import { noteCachedResponse, noteFreshResponse, errorMessage, clearOfflineCache, cachedPaths, notifyReconnect } from './offline';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
});

// Attach the token for whichever role the current page belongs to
// (/coach/* -> coach session, /parent/* -> parent session). Both sessions can
// exist at once; the URL decides which one a request speaks for.
api.interceptors.request.use((config) => {
  const role = activeRole();
  const token = role ? readSession(role)?.token : null;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // v2.4: the service worker marks responses served from its cache.
    // #112: tracked per path, so an unrelated fresh read can't hide it.
    const cached = response.headers?.['x-hh-cache'];
    const path = response.config?.url || '*';
    if (cached) noteCachedResponse(cached === '1' ? null : cached, path); else noteFreshResponse(path);
    return response;
  },
  (error) => {
    // v2.4 AD2: a write while offline fails with a clear, typed error.
    if (!error.response && typeof navigator !== 'undefined' && navigator.onLine === false) {
      error.offline = true;
      error.message = "You're offline. Reconnect and try again — nothing was saved.";
    }
    if (error.response?.data?.code === 'offline') error.offline = true;
    // 401/403 mean the session is invalid — clear it and bounce to the
    // landing page. A 402 (subscription_required) is deliberately NOT handled
    // here: the coach is still authenticated, just unpaid. We keep them logged
    // in so they can reach Settings and subscribe; pages surface it inline and
    // CoachLayout shows the paywall banner.
    // Only the JWT middleware's own answers (401 auth_required, 403
    // auth_invalid) mean the session is dead. A 403 'forbidden' or
    // 'registration_closed' is an authorization answer to a VALID session
    // and must not log the user out.
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const sessionDead = status === 401 || code === 'auth_invalid';
    if (sessionDead) {
      const role = activeRole();
      if (role) clearSession(role);
      clearOfflineCache(); // X-20: a dead session (revoked, deleted) leaves nothing cached behind
      window.location.href = loginPathFor(role);
    }
    return Promise.reject(error);
  }
);

// H-09: back online, tell the pages on screen to reload. A path still marked
// as saved data a few seconds later (one no page reloads, e.g. a badge poll)
// is read again here, so the "reconnecting…" banner cannot stick.
export const RECONNECT_FALLBACK_MS = 3000;
export const refreshAfterReconnect = () => {
  if (!cachedPaths().length) return;
  notifyReconnect();
  setTimeout(() => { cachedPaths().forEach((path) => { api.get(path).catch(() => {}); }); }, RECONNECT_FALLBACK_MS);
};
if (typeof window !== 'undefined') window.addEventListener('online', refreshAfterReconnect);

// True when a request was blocked by the subscription gate (HTTP 402 with our
// code). Use this in page-level catch blocks to show coach-facing billing copy
// instead of the neutral server message (which is worded for parents).
export const isSubscriptionError = (error) =>
  error?.response?.status === 402 &&
  error?.response?.data?.code === 'subscription_required';

export const SUBSCRIPTION_BLOCKED_MESSAGE =
  'Your HoopHero trial has ended. Subscribe in Settings to keep managing your team.';

// X-10: text for a failed coach-side save. Subscription gate first, then the
// first field-level validation message, then offline / the server's own
// error, then the caller's fallback.
export const saveErrorMessage = (error, fallback = 'Could not save. Please try again.') => {
  if (isSubscriptionError(error)) return SUBSCRIPTION_BLOCKED_MESSAGE;
  const fields = error?.response?.data?.fields;
  if (fields && typeof fields === 'object' && Object.values(fields).length) return String(Object.values(fields)[0]);
  return errorMessage(error, fallback);
};

export const authAPI = {
  coachLogin: (data) => api.post('/auth/coach/login', data),
  coachRegister: (data) => api.post('/auth/coach/register', data),
  parentLogin: (data) => api.post('/auth/parent/login', data),
  parentRegister: (data) => api.post('/auth/parent/register', data),
  lookupProgram: (code) => api.get(`/auth/program/${code}`),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const playersAPI = {
  updateOwnChild: (playerId, data) => api.put(`/players/${playerId}/family`, data), // v2.5 AD3
  getRoster: (programId) => api.get(`/players/roster/${programId}`),
  getFamilyPlayers: (familyId) => api.get(`/players/family/${familyId}`),
  addPlayer: (data) => api.post('/players/add', data),
  updatePlayer: (playerId, data) => api.put(`/players/update/${playerId}`, data),
  removePlayer: (playerId) => api.put(`/players/remove/${playerId}`),
};

export const gamesAPI = {
  // v2.2 Sprint 4: params.season = 'current' (default) | 'all' | <seasonId>
  getGames: (programId, params) => api.get(`/games/${programId}`, params ? { params } : undefined),
  addGame: (data) => api.post('/games/add', data),
  updateGame: (gameId, data) => api.put(`/games/update/${gameId}`, data),
  recordResult: (gameId, data) => api.put(`/games/result/${gameId}`, data),
  deleteGame: (gameId) => api.delete(`/games/delete/${gameId}`),
  // v2.1 Sprint 4
  rsvp: (gameId, playerId, status) => api.put(`/games/${gameId}/rsvp`, { playerId, status }),
  attendance: (gameId) => api.get(`/games/${gameId}/attendance`),
  setAttendance: (gameId, marks) => api.put(`/games/${gameId}/attendance`, { marks }),
  nudge: (gameId) => api.post(`/games/${gameId}/nudge`),
  // v2.2 Sprint 1: cancel / postpone / reschedule with family notice
  setStatus: (gameId, data) => api.post(`/games/${gameId}/status`, data),
};

// v2.8 Sprint 4 (AD4): live scoreboard behind FEATURE_SCOREBOARD. `public`
// needs no login: the request interceptor only adds a token when a session
// exists, and the endpoint ignores it either way.
export const scoreboardAPI = {
  get: (gameId) => api.get(`/scoreboard/games/${gameId}`),
  start: (gameId) => api.post(`/scoreboard/games/${gameId}/start`),
  score: (gameId, periods) => api.put(`/scoreboard/games/${gameId}/score`, { periods }),
  stats: (gameId, stats) => api.put(`/scoreboard/games/${gameId}/stats`, { stats }),
  final: (gameId) => api.post(`/scoreboard/games/${gameId}/final`),
  rotate: (gameId) => api.post(`/scoreboard/games/${gameId}/token/rotate`),
  public: (token) => api.get(`/scoreboard/public/${encodeURIComponent(token)}`),
};

export const programsAPI = {
  getSettings: (programId) => api.get(`/programs/${programId}`),
  updateSettings: (programId, data) => api.put(`/programs/${programId}`, data),
  // v2.8 F6 organizations (FEATURE_ORGS)
  mine: () => api.get('/me/programs'),
  create: (data) => api.post('/me/programs', data),
  switchTo: (programId) => api.post(`/me/programs/${programId}/switch`),
};

export const billingAPI = {
  createCheckout: (data) => api.post('/billing/checkout', data),
  getStatus: (coachId) => api.get(`/billing/status/${coachId}`),
  createPortal: (data) => api.post('/billing/portal', data),
};

export const coachesAPI = {
  getCoach: (coachId) => api.get(`/coaches/${coachId}`),
  updateCoach: (coachId, data) => api.put(`/coaches/${coachId}`, data),
  changePassword: (coachId, data) => api.put(`/coaches/${coachId}/password`, data),
};

export const parentsAPI = {
  getParent: (familyId) => api.get(`/parents/${familyId}`),
  updateParent: (familyId, data) => api.put(`/parents/${familyId}`, data),
  changePassword: (familyId, data) => api.put(`/parents/${familyId}/password`, data),
};

export const messagesAPI = {
  send: (data) => api.post('/messages/send', data),
  getCoachMessages: (programId) => api.get(`/messages/coach/${programId}`),
  getFamilyMessages: (familyId) => api.get(`/messages/family/${familyId}`),
  markRead: (messageId) => api.put(`/messages/${messageId}/read`),
  // v2.1 two-way messaging
  reply: (data) => api.post('/messages/reply', data),
  inbox: (programId) => api.get(`/messages/inbox/${programId}`),
  unreadCount: (programId) => api.get(`/messages/unread-count/${programId}`),
  thread: (familyId) => api.get(`/messages/thread/${familyId}`),
  markThreadRead: (familyId) => api.put(`/messages/thread/${familyId}/read`),
};

// R-19: the coach sidebar badge polls the unread count; pages that read or
// answer a conversation fire this so the badge refreshes right away.
export const MESSAGES_READ_EVENT = 'hh:messages-read';
export const notifyMessagesRead = () => {
  try { window.dispatchEvent(new Event(MESSAGES_READ_EVENT)); } catch (e) { /* non-browser */ }
};

export const paymentsAPI = {
  setPaid: (playerId, month, paid) => api.put(`/payments/${playerId}/${month}`, { paid }),
  programMonth: (programId, month) => api.get(`/payments/program/${programId}`, { params: month ? { month } : {} }),
  closeMonth: (programId) => api.post(`/payments/program/${programId}/close`),
  familyHistory: (familyId) => api.get(`/payments/family/${familyId}`),
};

export const pushAPI = {
  publicKey: () => api.get('/push/public-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.delete('/push/subscribe', { data: { endpoint } }),
};

// v2.8 F2 team chat (FEATURE_CHAT)
export const chatAPI = {
  list: (params) => api.get('/chat', { params }),
  post: (body) => api.post('/chat', { body }),
  markRead: () => api.post('/chat/read'),
  unread: () => api.get('/chat/unread'),
  seen: (id) => api.get(`/chat/${id}/seen`),
  remove: (id) => api.delete(`/chat/${id}`),
  mute: (memberType, memberId, muted) => api.put(`/chat/members/${memberType}/${memberId}/mute`, { muted }),
  updateMe: (data) => api.put('/chat/me', data),
};

export const configAPI = {
  get: () => api.get('/config'),
};

// v2.5 Sprint 4 (AD9): owner console, reads only
export const adminAPI = {
  overview: () => api.get('/admin/overview'),
  programs: (q = '') => api.get('/admin/programs', { params: { q } }),
  program: (programId) => api.get(`/admin/programs/${programId}`),
  families: (q) => api.get('/admin/families', { params: { q } }),
  ops: () => api.get('/admin/ops'),
  usage: (days = 7) => api.get('/admin/usage', { params: { days } }), // v2.7 US-4.1
  audit: (params = {}) => api.get('/admin/audit', { params }),
  exportCsv: (entity) => api.get(`/admin/export/${entity}.csv`, { responseType: 'blob' }),
  health: () => api.get('/health', { validateStatus: () => true }), // v2.6 AD6: 503 is still an answer
};

// v2.2 Sprint 3: saved venues (AD6) and iCal subscription (AD8)
export const venuesAPI = {
  list: (programId) => api.get(`/venues/${programId}`),
  create: (data) => api.post('/venues', data),
  update: (venueId, data) => api.put(`/venues/${venueId}`, data),
  remove: (venueId) => api.delete(`/venues/${venueId}`),
};

// v2.3: balance ledger (charges + payment entries)
export const ledgerAPI = {
  summary: (programId, month) => api.get(`/ledger/${programId}/summary`, month ? { params: { month } } : undefined),
  recordPayment: (programId, data) => api.post(`/ledger/${programId}/payments`, data),
  updatePayment: (entryId, data) => api.put(`/ledger/payments/${entryId}`, data),
  deletePayment: (entryId) => api.delete(`/ledger/payments/${entryId}`),
  createCharge: (programId, data) => api.post(`/ledger/${programId}/charges`, data),
  updateCharge: (chargeId, data) => api.put(`/ledger/charges/${chargeId}`, data),
  deleteCharge: (chargeId) => api.delete(`/ledger/charges/${chargeId}`),
  // v2.3 Sprint 4: CSV export (blob; the caller triggers the download)
  exportCsv: (programId, params) => api.get(`/ledger/${programId}/export.csv`, { params, responseType: 'blob' }),
};

// Save a blob response as a file (viewer sandboxes may block; caller ignores errors).
export const saveBlobResponse = (res, fallbackName = 'export.csv') => {
  try {
    const cd = res.headers?.['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/);
    const name = (m && m[1]) || fallbackName;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return name;
  } catch (e) { return null; }
};

// v2.4: computed Getting Started checklist (AD6)
// v2.5: legal status / re-acceptance and parent self-edit of a child
// v2.5 F2: program waivers (behind FEATURE_WAIVERS)
export const waiversAPI = {
  template: () => api.get('/waivers/template'),
  get: (programId) => api.get(`/waivers/${programId}`),
  upsert: (programId, data) => api.put(`/waivers/${programId}`, data),
  retire: (programId) => api.delete(`/waivers/${programId}`),
  sign: (waiverId, data) => api.post(`/waivers/${waiverId}/sign`, data),
};

export const meAPI = {
  legal: () => api.get('/me/legal'),
  acceptLegal: () => api.post('/me/accept-legal'),
  // v2.5 Sprint 3 (AD6/AD7): self-serve export and deletion
  exportJson: () => api.get('/me/export', { responseType: 'blob' }),
  exportZip: () => api.get('/me/export.zip', { responseType: 'blob' }),
  deleteAccount: (payload) => api.delete('/me', { data: payload }),
};

// v2.7 US-5.1: "Send feedback" (FEATURE_FEEDBACK)
export const feedbackAPI = {
  send: (message, page) => api.post('/feedback', { message, page }),
};

export const onboardingAPI = {
  checklist: (programId) => api.get(`/onboarding/${programId}`),
  flag: (flag) => api.post('/onboarding/flag', { flag }),
  dismiss: () => api.post('/onboarding/dismiss'),
};

export const seasonsAPI = {
  list: (programId) => api.get(`/seasons/${programId}`),
  start: (data) => api.post('/seasons', data),
  update: (seasonId, data) => api.put(`/seasons/${seasonId}`, data),
};

export const calendarAPI = {
  link: () => api.get('/calendar/link'),
  regenerate: () => api.post('/calendar/regenerate'),
};

// v2.8 F5: albums (behind FEATURE_ALBUMS). The file itself goes straight to
// Storage: the page PUTs it to uploadUrl, then confirms with completeUpload.
export const mediaAPI = {
  albums: () => api.get('/media/albums'),
  album: (albumId, before) => api.get(`/media/albums/${albumId}`, before ? { params: { before } } : undefined),
  startUpload: (data) => api.post('/media/uploads', data),
  completeUpload: (id, data) => api.post(`/media/uploads/${id}/complete`, data || {}),
  remove: (id) => api.delete(`/media/${id}`),
};

// v2.8 Sprint 3 (AD3): volunteer slots and carpools per event. Every write
// answers with the refreshed { slots, carpools, mine } for that event.
export const signupsAPI = {
  forGame: (gameId) => api.get(`/signups/game/${gameId}`),
  addSlots: (gameId, slots) => api.post(`/signups/game/${gameId}/slots`, { slots }),
  addTemplate: (gameId) => api.post(`/signups/game/${gameId}/slots/template`),
  updateSlot: (slotId, data) => api.put(`/signups/slots/${slotId}`, data),
  deleteSlot: (slotId) => api.delete(`/signups/slots/${slotId}`),
  claim: (slotId) => api.post(`/signups/slots/${slotId}/claim`),
  release: (slotId) => api.delete(`/signups/slots/${slotId}/claim`),
  offerRide: (gameId, data) => api.post(`/signups/game/${gameId}/carpools`, data),
  deleteOffer: (offerId) => api.delete(`/signups/carpools/${offerId}`),
  requestRide: (offerId, riders) => api.post(`/signups/carpools/${offerId}/requests`, { riders }),
  releaseRide: (offerId) => api.delete(`/signups/carpools/${offerId}/requests`),
};

export const ymcasAPI = {
  states: () => api.get('/ymcas/states'),
  search: (q, state) => api.get('/ymcas', { params: { ...(q ? { q } : {}), ...(state ? { state } : {}) } }),
};

export default api;